/**
 * SortableBlock - A draggable content block
 */

import React, { useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ContentBlock, TextBlock, ImageBlock, ButtonBlock, DividerBlock, SpacerBlock, FooterBlock, VaccineTableBlock, HeadingLevel, FONT_FAMILIES, HEADING_FONT_SIZES, VaccineListType } from './types';
import { Trash2, GripVertical, Upload, Loader2, Facebook, Instagram, Linkedin, ChevronDown, Bold, AlignLeft, AlignCenter, AlignRight, Type, Link2, Image } from 'lucide-react';
import { uploadEmailImage } from '../../../utils/notificationsBroadcastApi';

// Custom X (formerly Twitter) icon since lucide-react doesn't have it
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface SortableBlockProps {
  block: ContentBlock;
  columnId: string;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onUpdate: (updates: Partial<ContentBlock>) => void;
  onHover?: (blockId: string | null) => void;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}

export const SortableBlock: React.FC<SortableBlockProps> = ({
  block,
  columnId,
  isSelected,
  onSelect,
  onDelete,
  onUpdate,
  onHover,
  isFirstRow = false,
  isLastColumn = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: block.id,
    data: { 
      type: 'block', 
      blockId: block.id, 
      columnId: columnId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isHovered, setIsHovered] = useState(false);
  
  const renderBlockContent = () => {
    switch (block.type) {
      case 'text':
        return <TextBlockRenderer block={block} onUpdate={onUpdate} isSelected={isSelected} isFirstRow={isFirstRow} isLastColumn={isLastColumn} />;
      case 'image':
        return <ImageBlockRenderer block={block} onUpdate={onUpdate} isSelected={isSelected} isFirstRow={isFirstRow} isLastColumn={isLastColumn} />;
      case 'button':
        return <ButtonBlockRenderer block={block} isSelected={isSelected} onUpdate={onUpdate} isFirstRow={isFirstRow} isLastColumn={isLastColumn} />;
      case 'divider':
        return <DividerBlockRenderer block={block} />;
      case 'spacer':
        return <SpacerBlockRenderer block={block} />;
      case 'footer':
        return <FooterBlockRenderer block={block} onUpdate={onUpdate} isSelected={isSelected} isFirstRow={isFirstRow} isLastColumn={isLastColumn} />;
      case 'vaccine_table':
        return <VaccineTableBlockRenderer block={block} onUpdate={onUpdate} isSelected={isSelected} isFirstRow={isFirstRow} isLastColumn={isLastColumn} />;
      default:
        return <div>Unknown block type</div>;
    }
  };

  const showControls = isSelected || isHovered || isDragging;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'opacity-50 z-50' : ''} ${
        isSelected ? 'ring-2 ring-blue-500' : isHovered ? 'ring-1 ring-blue-300' : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setIsHovered(true);
        onHover?.(block.id);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onHover?.(null);
      }}
    >
      {/* Block Controls - only visible when this block is hovered or selected */}
      <div
        className={`absolute -left-8 top-0 bottom-0 flex flex-col items-center justify-center gap-1.5 transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 cursor-grab active:cursor-grabbing rounded shadow-md"
          style={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid #000000' }}
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1.5 rounded shadow-md hover:bg-red-100"
          style={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid #000000' }}
          title="Delete block"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Block Content */}
      {renderBlockContent()}
    </div>
  );
};

// =============================================================================
// Block Renderers
// =============================================================================

// Map font weight value to CSS number
const getFontWeightValue = (weight: string): number => {
  switch (weight) {
    case 'normal': return 400;
    case 'medium': return 500;
    case 'semibold': return 600;
    case 'bold': return 700;
    default: return 400;
  }
};

const TextBlockRenderer: React.FC<{
  block: TextBlock;
  onUpdate: (updates: Partial<TextBlock>) => void;
  isSelected: boolean;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}> = ({ block, onUpdate, isSelected, isFirstRow = false, isLastColumn = false }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const wasSelectedRef = useRef(false);
  const lastContentRef = useRef(block.content);
  const fontFamily = block.fontFamily || 'Arial';
  const letterSpacing = block.letterSpacing || 0;
  const fontWeight = getFontWeightValue(block.fontWeight || 'normal');
  
  // Dropdown states for floating toolbar
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showVariableDropdown, setShowVariableDropdown] = useState(false);
  
  const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
  
  // Available template variables - matches the top bar
  const templateVariables = [
    { key: 'pet_owner_name', label: 'Owner Name' },
    { key: 'pet_owner_first_name', label: 'Owner First' },
    { key: 'pet_owner_email', label: 'Owner Email' },
    { key: 'pet_owner_phone', label: 'Owner Phone' },
    { key: 'owner_pet_name', label: 'Pet Name' },
    { key: 'has_have', label: 'Has/Have' },
    { key: 'is/are', label: 'Is/Are' },
    { key: 'practice_name', label: 'Practice Name' },
    { key: 'practice_phone', label: 'Practice Phone' },
    { key: 'practice_email', label: 'Practice Email' },
    { key: 'practice_address', label: 'Practice Addr' },
    { key: 'next_appointment_date', label: 'Next Appt Date' },
    { key: 'form_link', label: 'Form Link' },
    { key: 'book_appointment_link', label: 'Book Appt Link' },
  ];
  
  // Insert variable at cursor position
  const insertVariable = (variableKey: string) => {
    if (!textRef.current) return;
    
    const selection = window.getSelection();
    const variableText = `{{${variableKey}}} `;
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Delete any selected text
      range.deleteContents();
      // Insert the variable text
      const textNode = document.createTextNode(variableText);
      range.insertNode(textNode);
      // Move cursor after the inserted text
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // No selection, append at the end
      textRef.current.textContent += variableText;
    }
    
    // Trigger input event to sync state
    const inputEvent = new Event('input', { bubbles: true });
    textRef.current.dispatchEvent(inputEvent);
    
    // Focus the editor
    textRef.current.focus();
    
    setShowVariableDropdown(false);
  };
  
  const getHeadingLabel = () => {
    switch (block.headingLevel) {
      case 'h1': return 'Heading 1';
      case 'h2': return 'Heading 2';
      case 'h3': return 'Heading 3';
      case 'p': return 'Paragraph';
      default: return 'Paragraph';
    }
  };

  
  // Convert newlines to <br> tags for display
  const getDisplayHtml = (content: string) => {
    if (!content) return '';
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped.replace(/\n/g, '<br>');
  };

  // Set content when becoming selected (to enable editing)
  React.useEffect(() => {
    if (isSelected && !wasSelectedRef.current && textRef.current) {
      // Just became selected - set the HTML content for editing
      textRef.current.innerHTML = getDisplayHtml(block.content);
      lastContentRef.current = block.content;
    }
    wasSelectedRef.current = isSelected;
  }, [isSelected, block.content]);

  // Sync content when block.content changes externally (e.g., variable insertion)
  React.useEffect(() => {
    if (isSelected && textRef.current && block.content !== lastContentRef.current) {
      // Content changed externally - update the contentEditable
      const selection = window.getSelection();
      const hadFocus = document.activeElement === textRef.current;
      
      // Save cursor position
      let cursorOffset = 0;
      if (hadFocus && selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        cursorOffset = range.startOffset;
      }
      
      // Update content
      textRef.current.innerHTML = getDisplayHtml(block.content);
      lastContentRef.current = block.content;
      
      // Restore focus and move cursor to end
      if (hadFocus && textRef.current) {
        textRef.current.focus();
        // Move cursor to end of content
        const newRange = document.createRange();
        newRange.selectNodeContents(textRef.current);
        newRange.collapse(false); // collapse to end
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
    }
  }, [block.content, isSelected]);
  
  // Extract text from contentEditable, preserving newlines
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    let html = element.innerHTML;
    
    // Convert HTML line breaks to newlines
    html = html.replace(/<br\s*\/?>/gi, '\n');
    html = html.replace(/<\/div>\s*<div>/gi, '\n');
    html = html.replace(/<div>/gi, '\n');
    html = html.replace(/<\/div>/gi, '');
    html = html.replace(/<\/p>\s*<p>/gi, '\n\n');
    html = html.replace(/<p>/gi, '');
    html = html.replace(/<\/p>/gi, '');
    // Strip remaining HTML tags
    html = html.replace(/<[^>]*>/g, '');
    // Decode HTML entities
    const temp = document.createElement('div');
    temp.innerHTML = html;
    let text = temp.textContent || '';
    // Clean up leading newline
    text = text.replace(/^\n/, '');
    
    lastContentRef.current = text;
    onUpdate({ content: text });
  };

  // Handle input to sync state with contentEditable
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    let html = element.innerHTML;
    
    // Convert HTML line breaks to newlines
    html = html.replace(/<br\s*\/?>/gi, '\n');
    html = html.replace(/<\/div>\s*<div>/gi, '\n');
    html = html.replace(/<div>/gi, '\n');
    html = html.replace(/<\/div>/gi, '');
    html = html.replace(/<\/p>\s*<p>/gi, '\n\n');
    html = html.replace(/<p>/gi, '');
    html = html.replace(/<\/p>/gi, '');
    html = html.replace(/<[^>]*>/g, '');
    
    const temp = document.createElement('div');
    temp.innerHTML = html;
    let text = temp.textContent || '';
    text = text.replace(/^\n/, '');
    
    lastContentRef.current = text;
  };
  
  return (
    <div className="relative" style={{ zIndex: isSelected ? 10 : 'auto' }}>
      {/* Floating Format Toolbar - 1 row */}
      {isSelected && (
        <div 
          className={`absolute z-50 ${isLastColumn ? 'right-0' : 'left-0'} ${isFirstRow ? 'top-full mt-2' : '-top-12'}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-0.5 px-2 py-1.5 bg-slate-800 rounded-lg shadow-xl">
            {/* Heading Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowHeadingDropdown(!showHeadingDropdown);
                  setShowFontDropdown(false);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
              >
                <span className="truncate max-w-[80px]">{getHeadingLabel()}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showHeadingDropdown && (
                <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                  {(['h1', 'h2', 'h3', 'p'] as HeadingLevel[]).map((level) => (
                    <button
                      key={level}
                      onClick={() => {
                        onUpdate({ 
                          headingLevel: level,
                          fontSize: HEADING_FONT_SIZES[level],
                          fontWeight: level === 'p' ? 'normal' : 'bold',
                        });
                        setShowHeadingDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.headingLevel === level ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      {level === 'h1' && <span className="font-bold text-lg">Heading 1</span>}
                      {level === 'h2' && <span className="font-bold text-base">Heading 2</span>}
                      {level === 'h3' && <span className="font-semibold">Heading 3</span>}
                      {level === 'p' && <span>Paragraph</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Font Family */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowFontDropdown(!showFontDropdown);
                  setShowHeadingDropdown(false);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
              >
                <span className="truncate max-w-[70px]" style={{ fontFamily: block.fontFamily }}>
                  {block.fontFamily || 'Arial'}
                </span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showFontDropdown && (
                <div className="absolute top-full left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                  {FONT_FAMILIES.map((font) => (
                    <button
                      key={font.value}
                      onClick={() => {
                        onUpdate({ fontFamily: font.value });
                        setShowFontDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontFamily === font.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Font Size */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSizeDropdown(!showSizeDropdown);
                  setShowHeadingDropdown(false);
                  setShowFontDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors min-w-[45px]"
              >
                <span>{block.fontSize || 16}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showSizeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-16 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                  {fontSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => {
                        onUpdate({ fontSize: size });
                        setShowSizeDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Bold */}
            <button
              onClick={() => onUpdate({ fontWeight: block.fontWeight === 'bold' ? 'normal' : 'bold' })}
              className={`p-1.5 rounded transition-colors ${
                block.fontWeight === 'bold' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'
              }`}
              title="Bold"
            >
              <Bold className="w-4 h-4" />
            </button>

            <button className="p-1.5 text-slate-500 rounded cursor-not-allowed" title="Italic (coming soon)" disabled>
              <span className="text-sm italic font-serif">I</span>
            </button>

            <button className="p-1.5 text-slate-500 rounded cursor-not-allowed" title="Underline (coming soon)" disabled>
              <span className="text-sm underline">U</span>
            </button>

            {/* Text Color */}
            <div className="relative p-1.5">
              <input
                type="color"
                value={block.color || '#000000'}
                onChange={(e) => onUpdate({ color: e.target.value })}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                title="Text Color"
              />
              <div className="relative">
                <Type className="w-4 h-4 text-slate-300" />
                <div className="absolute -bottom-0.5 left-0 right-0 h-1 rounded-sm" style={{ backgroundColor: block.color || '#000000' }} />
              </div>
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Alignment */}
            <button
              onClick={() => onUpdate({ textAlign: 'left' })}
              className={`p-1.5 rounded transition-colors ${block.textAlign === 'left' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onUpdate({ textAlign: 'center' })}
              className={`p-1.5 rounded transition-colors ${block.textAlign === 'center' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={() => onUpdate({ textAlign: 'right' })}
              className={`p-1.5 rounded transition-colors ${block.textAlign === 'right' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Insert Variable Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowVariableDropdown(!showVariableDropdown);
                  setShowHeadingDropdown(false);
                  setShowFontDropdown(false);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
                title="Add Variable"
              >
                <span>Variable</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showVariableDropdown && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-64 overflow-y-auto">
                  {templateVariables.map((variable) => (
                    <button
                      key={variable.key}
                      onClick={() => insertVariable(variable.key)}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {variable.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isSelected ? (
        // Edit mode: contentEditable div that React doesn't control
        <div
          ref={textRef}
          style={{
            fontFamily: `${fontFamily}, sans-serif`,
            fontSize: block.fontSize,
            fontWeight: fontWeight,
            textAlign: block.textAlign,
            color: block.color,
            lineHeight: block.lineHeight,
            letterSpacing: letterSpacing > 0 ? `${letterSpacing}px` : undefined,
            padding: '8px',
            backgroundColor: block.backgroundColor || 'transparent',
            marginBottom: 0,
            minHeight: '2em',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
          contentEditable
          suppressContentEditableWarning
          onBlur={handleBlur}
          onInput={handleInput}
          className={`outline-none ring-2 ring-blue-500 ${!block.backgroundColor ? 'bg-blue-50/30' : ''}`}
        />
      ) : (
        // Display mode: React controls the content
        <div
          style={{
            fontFamily: `${fontFamily}, sans-serif`,
            fontSize: block.fontSize,
            fontWeight: fontWeight,
            textAlign: block.textAlign,
            color: block.color,
            lineHeight: block.lineHeight,
            letterSpacing: letterSpacing > 0 ? `${letterSpacing}px` : undefined,
            padding: '8px',
            backgroundColor: block.backgroundColor || 'transparent',
            marginBottom: 0,
            minHeight: '1em',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
          className="outline-none"
          dangerouslySetInnerHTML={{ __html: getDisplayHtml(block.content) }}
        />
      )}
    </div>
  );
};

const ImageBlockRenderer: React.FC<{ 
  block: ImageBlock;
  onUpdate: (updates: Partial<ImageBlock>) => void;
  isSelected: boolean;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}> = ({ block, onUpdate, isSelected, isFirstRow = false, isLastColumn = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [isEditingAlt, setIsEditingAlt] = useState(false);
  const [tempLink, setTempLink] = useState(block.link || '');
  const [tempAlt, setTempAlt] = useState(block.alt || '');
  const [isDragOver, setIsDragOver] = useState(false);
  
  const isPlaceholder = block.src.includes('placehold.co');
  
  const widthOptions = [25, 50, 75, 100];
  
  const handleUploadFile = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    
    setIsUploading(true);
    try {
      const publicUrl = await uploadEmailImage(file);
      onUpdate({ src: publicUrl, alt: file.name });
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUploadFile(file);
  };
  
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    // Check for files in dataTransfer
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await handleUploadFile(file);
      }
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // Must prevent default to allow drop
    e.preventDefault();
    e.stopPropagation();
    // Only show drop state for file drags
    if (e.dataTransfer?.types?.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };
  
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragOver(true);
    }
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only reset if leaving the container (not entering a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };
  
  return (
    <div className="relative" style={{ zIndex: isSelected ? 10 : 'auto' }}>
      {/* Floating Image Toolbar - 1 row */}
      {isSelected && (
        <div 
          className={`absolute z-50 ${isLastColumn ? 'right-0' : 'left-0'} ${isFirstRow ? 'top-full mt-2' : '-top-12'}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 rounded-lg shadow-xl">
              {/* Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded"
              >
                <Upload className="w-3 h-3" />
                {isPlaceholder ? 'Upload' : 'Replace'}
              </button>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Width Options */}
              <span className="text-xs text-slate-400">Width:</span>
              {widthOptions.map((width) => (
                <button
                  key={width}
                  onClick={() => onUpdate({ widthValue: width, widthUnit: '%' })}
                  className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                    block.widthValue === width && block.widthUnit === '%'
                      ? 'bg-slate-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {width}%
                </button>
              ))}

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Alignment */}
              <button
                onClick={() => onUpdate({ align: 'left' })}
                className={`p-1 rounded transition-colors ${block.align === 'left' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => onUpdate({ align: 'center' })}
                className={`p-1 rounded transition-colors ${block.align === 'center' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4" />
              </button>
              <button
                onClick={() => onUpdate({ align: 'right' })}
                className={`p-1 rounded transition-colors ${block.align === 'right' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Right"
              >
                <AlignRight className="w-4 h-4" />
              </button>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Link */}
              <div className="flex items-center gap-1">
                <Link2 className="w-3 h-3 text-slate-400" />
                {isEditingLink ? (
                  <input
                    type="text"
                    value={tempLink}
                    onChange={(e) => setTempLink(e.target.value)}
                    onBlur={() => {
                      onUpdate({ link: tempLink || undefined });
                      setIsEditingLink(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onUpdate({ link: tempLink || undefined });
                        setIsEditingLink(false);
                      }
                    }}
                    className="w-28 px-1.5 py-0.5 text-xs bg-slate-700 text-white border border-slate-600 rounded"
                    placeholder="https://..."
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => setIsEditingLink(true)}
                    className="px-1.5 py-0.5 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded truncate max-w-[100px]"
                  >
                    {block.link || 'Add link'}
                  </button>
                )}
              </div>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Alt Text */}
              <div className="flex items-center gap-1">
                <Image className="w-3 h-3 text-slate-400" />
                {isEditingAlt ? (
                  <input
                    type="text"
                    value={tempAlt}
                    onChange={(e) => setTempAlt(e.target.value)}
                    onBlur={() => {
                      onUpdate({ alt: tempAlt });
                      setIsEditingAlt(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onUpdate({ alt: tempAlt });
                        setIsEditingAlt(false);
                      }
                    }}
                    className="w-28 px-1.5 py-0.5 text-xs bg-slate-700 text-white border border-slate-600 rounded"
                    placeholder="Alt text..."
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => setIsEditingAlt(true)}
                    className="px-1.5 py-0.5 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded truncate max-w-[100px]"
                  >
                    {block.alt || 'Add alt'}
                  </button>
                )}
              </div>
          </div>
        </div>
      )}

      {/* Image Display with Drop Zone */}
      <div 
        style={{ textAlign: block.align, padding: '8px', backgroundColor: block.backgroundColor || 'transparent' }} 
        className={`relative group transition-all ${isDragOver ? 'ring-2 ring-blue-500 ring-offset-2 bg-blue-50 dark:bg-blue-900/20' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {isUploading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : isDragOver ? (
          <div 
            className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-blue-400 rounded-lg bg-blue-50"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <Upload className="w-8 h-8 text-blue-500 mb-2" />
            <span className="text-sm text-blue-600 font-medium">Drop image here</span>
          </div>
        ) : isPlaceholder ? (
          <div 
            className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <Upload className="w-8 h-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500 font-medium">Click or drag image here</span>
          </div>
        ) : (
          <img
            src={block.src}
            alt={block.alt}
            style={{
              width: `${block.widthValue}${block.widthUnit}`,
              height: block.heightValue !== null ? `${block.heightValue}${block.heightUnit}` : 'auto',
              maxWidth: '100%',
              display: 'inline-block',
              objectFit: 'cover',
            }}
          />
        )}
      </div>
    </div>
  );
};

const ButtonBlockRenderer: React.FC<{ 
  block: ButtonBlock; 
  isSelected: boolean;
  onUpdate: (updates: Partial<ButtonBlock>) => void;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}> = ({ block, isSelected, onUpdate, isFirstRow = false, isLastColumn = false }) => {
  const [isEditingText, setIsEditingText] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [tempText, setTempText] = useState(block.text);
  const [tempLink, setTempLink] = useState(block.link);

  const borderRadiusOptions = [0, 4, 8, 12, 20, 9999];

  return (
    <div className="relative" style={{ zIndex: isSelected ? 10 : 'auto' }}>
      {/* Floating Button Toolbar - 1 row */}
      {isSelected && (
        <div 
          className={`absolute z-50 ${isLastColumn ? 'right-0' : 'left-0'} ${isFirstRow ? 'top-full mt-2' : '-top-12'}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 rounded-lg shadow-xl">
              {/* Button Text */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Text:</span>
                {isEditingText ? (
                  <input
                    type="text"
                    value={tempText}
                    onChange={(e) => setTempText(e.target.value)}
                    onBlur={() => {
                      onUpdate({ text: tempText });
                      setIsEditingText(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onUpdate({ text: tempText });
                        setIsEditingText(false);
                      }
                    }}
                    className="w-24 px-1.5 py-0.5 text-xs bg-slate-700 text-white border border-slate-600 rounded"
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => setIsEditingText(true)}
                    className="px-1.5 py-0.5 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded truncate max-w-[80px]"
                  >
                    {block.text || 'Edit'}
                  </button>
                )}
              </div>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Button Link */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Link:</span>
                {isEditingLink ? (
                  <input
                    type="text"
                    value={tempLink}
                    onChange={(e) => setTempLink(e.target.value)}
                    onBlur={() => {
                      onUpdate({ link: tempLink });
                      setIsEditingLink(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onUpdate({ link: tempLink });
                        setIsEditingLink(false);
                      }
                    }}
                    className="w-32 px-1.5 py-0.5 text-xs bg-slate-700 text-white border border-slate-600 rounded"
                    placeholder="https://..."
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => setIsEditingLink(true)}
                    className="px-1.5 py-0.5 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded truncate max-w-[100px]"
                  >
                    {block.link || 'Add link'}
                  </button>
                )}
              </div>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Background Color */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">BG:</span>
                <div className="relative">
                  <input
                    type="color"
                    value={block.backgroundColor || '#3b82f6'}
                    onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
                    className="w-6 h-6 rounded cursor-pointer border border-slate-600"
                    title="Background Color"
                  />
                </div>
              </div>

              {/* Text Color */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400">Text:</span>
                <div className="relative">
                  <input
                    type="color"
                    value={block.textColor || '#ffffff'}
                    onChange={(e) => onUpdate({ textColor: e.target.value })}
                    className="w-6 h-6 rounded cursor-pointer border border-slate-600"
                    title="Text Color"
                  />
                </div>
              </div>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Border Radius */}
              <div className="flex items-center gap-0.5">
                {borderRadiusOptions.map((radius) => (
                  <button
                    key={radius}
                    onClick={() => onUpdate({ borderRadius: radius })}
                    className={`w-6 h-6 flex items-center justify-center text-xs rounded transition-colors ${
                      block.borderRadius === radius 
                        ? 'bg-slate-600 text-white' 
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                    title={radius === 9999 ? 'Pill' : `${radius}px`}
                  >
                    <div 
                      className="w-3 h-3 border border-current"
                      style={{ borderRadius: radius === 9999 ? '50%' : `${Math.min(radius, 4)}px` }}
                    />
                  </button>
                ))}
              </div>

              <div className="w-px h-5 bg-slate-600 mx-1" />

              {/* Alignment */}
              <button
                onClick={() => onUpdate({ align: 'left' })}
                className={`p-1 rounded transition-colors ${block.align === 'left' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => onUpdate({ align: 'center' })}
                className={`p-1 rounded transition-colors ${block.align === 'center' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4" />
              </button>
              <button
                onClick={() => onUpdate({ align: 'right' })}
                className={`p-1 rounded transition-colors ${block.align === 'right' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                title="Align Right"
              >
                <AlignRight className="w-4 h-4" />
              </button>
          </div>
        </div>
      )}

      {/* Button Display */}
      <div style={{ textAlign: block.align, padding: '8px' }}>
        <a
          href={block.link}
          onClick={(e) => e.preventDefault()}
          style={{
            display: 'inline-block',
            backgroundColor: block.backgroundColor,
            color: block.textColor,
            padding: `${block.padding.vertical}px ${block.padding.horizontal}px`,
            borderRadius: block.borderRadius,
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {block.text}
        </a>
      </div>
    </div>
  );
};

const DividerBlockRenderer: React.FC<{ block: DividerBlock }> = ({ block }) => {
  return (
    <div style={{ padding: '16px 8px', backgroundColor: block.backgroundColor || 'transparent' }}>
      <hr
        style={{
          border: 'none',
          borderTop: `${block.thickness}px ${block.style} ${block.color}`,
          width: `${block.width}%`,
          margin: '0 auto',
        }}
      />
    </div>
  );
};

const SpacerBlockRenderer: React.FC<{ block: SpacerBlock }> = ({ block }) => {
  return (
    <div
      style={{ height: block.height, backgroundColor: block.backgroundColor || 'transparent' }}
      className={`border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400 ${!block.backgroundColor ? 'bg-gray-50/50' : ''}`}
    >
      {block.height}px
    </div>
  );
};

const FooterBlockRenderer: React.FC<{
  block: FooterBlock;
  onUpdate: (updates: Partial<FooterBlock>) => void;
  isSelected: boolean;
  isFirstRow: boolean;
  isLastColumn: boolean;
}> = ({ block, onUpdate, isSelected, isFirstRow, isLastColumn }) => {
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  
  const fontSizes = [10, 12, 14, 16, 18, 20];
  const fontFamily = block.fontFamily || 'Arial';
  const fontSize = block.fontSize || 14;
  const fontWeight = getFontWeightValue(block.fontWeight || 'normal');
  
  const socialIcons = [
    { key: 'facebook', icon: Facebook, url: block.socialLinks.facebook },
    { key: 'instagram', icon: Instagram, url: block.socialLinks.instagram },
    { key: 'twitter', icon: XIcon, url: block.socialLinks.twitter },
    { key: 'linkedin', icon: Linkedin, url: block.socialLinks.linkedin },
  ].filter(s => s.url);

  return (
    <div className="relative" style={{ zIndex: isSelected ? 10 : 'auto' }}>
      {/* Floating Footer Toolbar - compact like text toolbar */}
      {isSelected && (
        <div 
          className={`absolute z-50 ${isLastColumn ? 'right-0' : 'left-0'} ${isFirstRow ? 'top-full mt-2' : '-top-12'}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-0.5 px-2 py-1.5 bg-slate-800 rounded-lg shadow-xl">
            {/* Font Family */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowFontDropdown(!showFontDropdown);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
              >
                <span className="truncate max-w-[70px]" style={{ fontFamily: block.fontFamily }}>
                  {block.fontFamily || 'Arial'}
                </span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showFontDropdown && (
                <div className="absolute top-full left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                  {FONT_FAMILIES.map((font) => (
                    <button
                      key={font.value}
                      onClick={() => {
                        onUpdate({ fontFamily: font.value });
                        setShowFontDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontFamily === font.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Font Size */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSizeDropdown(!showSizeDropdown);
                  setShowFontDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors min-w-[45px]"
              >
                <span>{block.fontSize || 14}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showSizeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-16 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                  {fontSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => {
                        onUpdate({ fontSize: size });
                        setShowSizeDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Bold */}
            <button
              onClick={() => onUpdate({ fontWeight: block.fontWeight === 'bold' ? 'normal' : 'bold' })}
              className={`p-1.5 rounded transition-colors ${
                block.fontWeight === 'bold' ? 'bg-slate-600 text-white' : 'text-slate-300 hover:bg-slate-700'
              }`}
              title="Bold"
            >
              <Bold className="w-4 h-4" />
            </button>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Colors Section Title */}
            <span className="text-xs text-slate-400 font-medium">Colors:</span>

            {/* Background Color */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1.5 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.backgroundColor }}
                />
                <span className="text-xs">Background</span>
                <input
                  type="color"
                  value={block.backgroundColor}
                  onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Text Color */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1.5 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.textColor }}
                />
                <span className="text-xs">Text</span>
                <input
                  type="color"
                  value={block.textColor}
                  onChange={(e) => onUpdate({ textColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Link Color */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1.5 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.linkColor }}
                />
                <span className="text-xs">Link</span>
                <input
                  type="color"
                  value={block.linkColor}
                  onChange={(e) => onUpdate({ linkColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Footer Preview */}
      <div
        style={{
          backgroundColor: block.backgroundColor,
          color: block.textColor,
          padding: '24px 16px',
          textAlign: 'center',
          fontFamily: `${fontFamily}, sans-serif`,
        }}
      >
        {/* Logo */}
        {block.logoUrl && (
          <div style={{ marginBottom: '16px' }}>
            <img 
              src={block.logoUrl} 
              alt="Logo" 
              style={{ 
                width: block.logoWidth, 
                height: block.logoAsAvatar ? block.logoWidth : 'auto',
                display: 'inline-block',
                borderRadius: block.logoAsAvatar ? '50%' : undefined,
                objectFit: block.logoAsAvatar ? 'cover' : undefined,
                border: block.logoAsAvatar ? `3px solid ${block.linkColor}` : undefined,
              }} 
            />
          </div>
        )}

        {/* Social Icons */}
        {block.showSocialIcons && socialIcons.length > 0 && (
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
            {socialIcons.map(({ key, icon: Icon, url }) => (
              <a
                key={key}
                href={url}
                onClick={(e) => e.preventDefault()}
                style={{ color: block.linkColor }}
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        )}

        {/* Contact Info - uses footer font settings */}
        {(block.contactEmail || block.contactPhone) && (
          <div style={{ marginBottom: '12px', fontSize: fontSize, fontWeight: fontWeight }}>
            {block.contactPhone && <span>{block.contactPhone}</span>}
            {block.contactPhone && block.contactEmail && <span> | </span>}
            {block.contactEmail && (
              <a href={`mailto:${block.contactEmail}`} style={{ color: block.linkColor }} onClick={(e) => e.preventDefault()}>
                {block.contactEmail}
              </a>
            )}
          </div>
        )}

        {/* Address - uses footer font settings */}
        {block.addressText && (
          <div style={{ marginBottom: '12px', fontSize: fontSize - 2, fontWeight: fontWeight }}>
            {block.addressText}
          </div>
        )}

        {/* Unsubscribe Link - fixed small size, not affected by font settings */}
        {block.unsubscribeText && (
          <div style={{ marginBottom: '12px', fontSize: '12px' }}>
            <a 
              href={block.unsubscribeUrl}
              onClick={(e) => e.preventDefault()}
              style={{ color: block.linkColor, textDecoration: 'underline' }}
            >
              {block.unsubscribeText}
            </a>
          </div>
        )}

        {/* Legal Text - fixed small size, not affected by font settings */}
        {block.legalText && (
          <div style={{ fontSize: '11px', opacity: 0.7 }}>
            {block.legalText}
          </div>
        )}
      </div>
    </div>
  );
};

const VaccineTableBlockRenderer: React.FC<{
  block: VaccineTableBlock;
  onUpdate: (updates: Partial<VaccineTableBlock>) => void;
  isSelected: boolean;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}> = ({ block, onUpdate, isSelected, isFirstRow = false, isLastColumn = false }) => {
  const fontFamily = block.fontFamily || 'Arial';
  const fontSize = block.fontSize || 14;
  const vaccineListType = block.vaccineListType || 'overdue';
  const overdueTextColor = block.overdueTextColor || '#dc2626';
  
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(block.title);
  
  const fontSizes = [10, 12, 14, 16, 18, 20];
  
  const vaccineTypeOptions: { value: VaccineListType; label: string; description: string }[] = [
    { value: 'overdue', label: 'Overdue Only', description: 'Past due vaccines' },
    { value: 'scheduled', label: 'Scheduled Only', description: 'Upcoming vaccines' },
    { value: 'all', label: 'All Vaccines', description: 'Both scheduled & overdue' },
  ];
  
  // Sample data for preview based on type
  const getSampleData = () => {
    switch (vaccineListType) {
      case 'scheduled':
        return [
          { petName: 'Max', vaccine: 'Rabies', dueDate: 'Jan 15, 2026', daysOverdue: null, status: 'Scheduled' },
          { petName: 'Bella', vaccine: 'DHPP', dueDate: 'Jan 20, 2026', daysOverdue: null, status: 'Scheduled' },
        ];
      case 'all':
        return [
          { petName: 'Max', vaccine: 'Rabies', dueDate: 'Jan 15, 2026', daysOverdue: null, status: 'Scheduled' },
          { petName: 'Bella', vaccine: 'DHPP', dueDate: 'Dec 20, 2025', daysOverdue: 17, status: 'Overdue' },
        ];
      default: // 'overdue'
        return [
          { petName: 'Max', vaccine: 'Rabies', dueDate: 'Dec 15, 2025', daysOverdue: 22, status: 'Overdue' },
          { petName: 'Bella', vaccine: 'DHPP', dueDate: 'Dec 20, 2025', daysOverdue: 17, status: 'Overdue' },
  ];
    }
  };
  
  const sampleData = getSampleData();
  
  // Determine column visibility based on vaccine type
  const showDaysOverdueColumn = block.showDaysOverdue && vaccineListType !== 'scheduled';
  const showStatusColumn = block.showStatus && vaccineListType !== 'overdue';
  
  const getVaccineTypeLabel = () => {
    return vaccineTypeOptions.find(o => o.value === vaccineListType)?.label || 'Overdue Only';
  };
  
  const getHelpText = () => {
    switch (vaccineListType) {
      case 'scheduled':
        return 'Shows upcoming scheduled vaccines when the email is sent.';
      case 'all':
        return 'Shows both scheduled and overdue vaccines when the email is sent.';
      default:
        return 'Shows overdue vaccines when the email is sent.';
    }
  };

  return (
    <div className="relative" style={{ zIndex: isSelected ? 10 : 'auto' }}>
      {/* Floating Vaccine Table Toolbar */}
      {isSelected && (
        <div 
          className={`absolute z-50 ${isLastColumn ? 'right-0' : 'left-0'} ${isFirstRow ? 'top-full mt-2' : '-top-12'}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-0.5 px-2 py-1.5 bg-slate-800 rounded-lg shadow-xl flex-wrap">
            {/* Vaccine Type Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowFontDropdown(false);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
              >
                <span className="truncate max-w-[100px]">{getVaccineTypeLabel()}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showTypeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                  {vaccineTypeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        onUpdate({ 
                          vaccineListType: option.value,
                          // Auto-adjust columns based on type
                          showDaysOverdue: option.value !== 'scheduled',
                          showStatus: option.value !== 'overdue',
                        });
                        setShowTypeDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                        vaccineListType === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Title */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">Title:</span>
              {isEditingTitle ? (
                <input
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  onBlur={() => {
                    onUpdate({ title: tempTitle });
                    setIsEditingTitle(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onUpdate({ title: tempTitle });
                      setIsEditingTitle(false);
                    }
                  }}
                  className="w-28 px-1.5 py-0.5 text-xs bg-slate-700 text-white border border-slate-600 rounded"
                  autoFocus
                />
              ) : (
                <button
                  onClick={() => {
                    setTempTitle(block.title);
                    setIsEditingTitle(true);
                  }}
                  className="px-1.5 py-0.5 text-xs text-white bg-slate-700 hover:bg-slate-600 rounded truncate max-w-[100px]"
                >
                  {block.title || 'Add title'}
                </button>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Font Family */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowFontDropdown(!showFontDropdown);
                  setShowTypeDropdown(false);
                  setShowSizeDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors"
              >
                <span className="truncate max-w-[60px]" style={{ fontFamily: block.fontFamily }}>
                  {block.fontFamily || 'Arial'}
                </span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showFontDropdown && (
                <div className="absolute top-full left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
                  {FONT_FAMILIES.map((font) => (
                    <button
                      key={font.value}
                      onClick={() => {
                        onUpdate({ fontFamily: font.value });
                        setShowFontDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontFamily === font.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Font Size */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSizeDropdown(!showSizeDropdown);
                  setShowTypeDropdown(false);
                  setShowFontDropdown(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-white hover:bg-slate-700 rounded transition-colors min-w-[40px]"
              >
                <span>{block.fontSize || 14}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
              {showSizeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-16 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                  {fontSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => {
                        onUpdate({ fontSize: size });
                        setShowSizeDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        block.fontSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-slate-600 mx-1" />

            {/* Colors */}
            <span className="text-xs text-slate-400">Colors:</span>
            
            {/* Header BG */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Header Background">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.headerBackgroundColor }}
                />
                <span className="text-[10px]">Header</span>
                <input
                  type="color"
                  value={block.headerBackgroundColor}
                  onChange={(e) => onUpdate({ headerBackgroundColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Header Text */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Header Text">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.headerTextColor }}
                />
                <span className="text-[10px]">H.Text</span>
                <input
                  type="color"
                  value={block.headerTextColor}
                  onChange={(e) => onUpdate({ headerTextColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Row BG */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Row Background">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.rowBackgroundColor }}
                />
                <span className="text-[10px]">Row</span>
                <input
                  type="color"
                  value={block.rowBackgroundColor}
                  onChange={(e) => onUpdate({ rowBackgroundColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Alt Row BG */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Alternate Row Background">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.alternateRowColor }}
                />
                <span className="text-[10px]">Alt</span>
                <input
                  type="color"
                  value={block.alternateRowColor}
                  onChange={(e) => onUpdate({ alternateRowColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Text Color */}
            <div className="relative group">
              <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Text Color">
                <div 
                  className="w-4 h-4 rounded border border-white/30" 
                  style={{ backgroundColor: block.textColor }}
                />
                <span className="text-[10px]">Text</span>
                <input
                  type="color"
                  value={block.textColor}
                  onChange={(e) => onUpdate({ textColor: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>

            {/* Overdue Text Color (only for overdue/all types) */}
            {vaccineListType !== 'scheduled' && (
              <div className="relative group">
                <label className="flex items-center gap-1 px-1 py-1 text-white hover:bg-slate-700 rounded cursor-pointer transition-colors" title="Overdue Days Color">
                  <div 
                    className="w-4 h-4 rounded border border-white/30" 
                    style={{ backgroundColor: overdueTextColor }}
                  />
                  <span className="text-[10px]">Overdue</span>
                  <input
                    type="color"
                    value={overdueTextColor}
                    onChange={(e) => onUpdate({ overdueTextColor: e.target.value })}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table Preview */}
    <div style={{ padding: '8px' }}>
      {block.title && (
        <h3 style={{
          fontFamily: `${fontFamily}, sans-serif`,
          fontSize: fontSize + 4,
          fontWeight: 600,
          color: block.textColor,
          margin: '0 0 12px 0',
        }}>
          {block.title}
        </h3>
      )}
      <table
        style={{
          width: '100%',
          fontFamily: `${fontFamily}, sans-serif`,
          fontSize: fontSize,
          color: block.textColor,
          border: `1px solid ${block.borderColor}`,
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          <tr style={{ backgroundColor: block.headerBackgroundColor, color: block.headerTextColor }}>
            {block.showPetName && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${block.borderColor}` }}>
                Pet Name
              </th>
            )}
            {block.showVaccineName && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${block.borderColor}` }}>
                Vaccine
              </th>
            )}
            {block.showScheduledDate && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${block.borderColor}` }}>
                  {vaccineListType === 'scheduled' ? 'Scheduled Date' : 'Due Date'}
              </th>
            )}
              {showStatusColumn && (
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${block.borderColor}` }}>
                  Status
                </th>
              )}
              {showDaysOverdueColumn && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${block.borderColor}` }}>
                Days Overdue
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sampleData.map((row, index) => (
            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? block.rowBackgroundColor : block.alternateRowColor }}>
              {block.showPetName && (
                <td style={{ padding: '10px 8px', borderBottom: `1px solid ${block.borderColor}` }}>{row.petName}</td>
              )}
              {block.showVaccineName && (
                <td style={{ padding: '10px 8px', borderBottom: `1px solid ${block.borderColor}` }}>{row.vaccine}</td>
              )}
              {block.showScheduledDate && (
                <td style={{ padding: '10px 8px', borderBottom: `1px solid ${block.borderColor}` }}>{row.dueDate}</td>
              )}
                {showStatusColumn && (
                  <td style={{ 
                    padding: '10px 8px', 
                    borderBottom: `1px solid ${block.borderColor}`,
                    color: row.status === 'Overdue' ? overdueTextColor : undefined,
                    fontWeight: row.status === 'Overdue' ? 600 : undefined,
                  }}>
                    {row.status}
                  </td>
                )}
                {showDaysOverdueColumn && row.daysOverdue !== null && (
                  <td style={{ 
                    padding: '10px 8px', 
                    borderBottom: `1px solid ${block.borderColor}`, 
                    color: overdueTextColor, 
                    fontWeight: 600 
                  }}>
                  {row.daysOverdue}
                </td>
              )}
                {showDaysOverdueColumn && row.daysOverdue === null && (
                  <td style={{ padding: '10px 8px', borderBottom: `1px solid ${block.borderColor}` }}>—</td>
                )}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
          {getHelpText()}
        </div>
      </div>
    </div>
  );
};
